class PrimerryProductGrid extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.rail = this.querySelector('[data-primerry-product-rail]');
    this.swiperElement = this.querySelector('[data-primerry-product-swiper]');
    this.prevButton = this.querySelector('[data-primerry-product-prev]');
    this.nextButton = this.querySelector('[data-primerry-product-next]');

    this.handleClick = this.handleClick.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('submit', this.handleSubmit);
    document.addEventListener('cart:lines-update', this.handleCartUpdate);

    this.querySelectorAll('[data-primerry-quick-dialog]').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('primerry-quick-view-open');
      });
    });

    this.initSwiper();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('submit', this.handleSubmit);
    document.removeEventListener('cart:lines-update', this.handleCartUpdate);
    this.swiper?.destroy(true, true);
  }

  handleClick(event) {
    const openButton = event.target.closest('[data-primerry-quick-open]');
    if (openButton) {
      const dialog = this.querySelector(`#${CSS.escape(openButton.dataset.primerryQuickOpen)}`);
      if (dialog && !dialog.open) {
        dialog.showModal();
        document.documentElement.classList.add('primerry-quick-view-open');
      }
      return;
    }

    const closeButton = event.target.closest('[data-primerry-quick-close]');
    if (closeButton) {
      closeButton.closest('dialog')?.close();
      document.documentElement.classList.remove('primerry-quick-view-open');
      return;
    }

    const thumb = event.target.closest('[data-primerry-quick-thumb]');
    if (thumb) {
      const dialog = thumb.closest('[data-primerry-quick-dialog]');
      const image = dialog?.querySelector('.primerry-quick-view__main-image-element');
      if (image && thumb.dataset.imageSrc) {
        image.src = thumb.dataset.imageSrc;
        image.srcset = '';
        dialog.querySelectorAll('[data-primerry-quick-thumb]').forEach((item) => {
          item.classList.toggle('is-active', item === thumb);
        });
      }
      return;
    }

  }

  async initSwiper() {
    if (!this.swiperElement || this.swiper) return;

    try {
      const SwiperClass = await this.waitForSwiper();
      if (!this.isConnected || this.swiper) return;

      this.swiper = new SwiperClass(this.swiperElement, {
        slidesPerView: 'auto',
        spaceBetween: 12,
        speed: 650,
        grabCursor: true,
        watchOverflow: true,
        threshold: 4,
        resistance: true,
        resistanceRatio: 0.72,
        longSwipesRatio: 0.15,
        longSwipesMs: 250,
        // Slide 40 feedback: a flick still glides with momentum, then settles on a card edge,
        // so the rail never stops with a card half cut against the content column.
        freeMode: {
          enabled: true,
          sticky: true,
          momentum: true,
          momentumBounce: true,
          momentumRatio: 0.85,
          momentumVelocityRatio: 0.8,
          minimumVelocity: 0.02,
        },
        // Only sideways trackpad or wheel gestures move the rail. A vertical wheel scrolls the
        // page as normal instead of being caught by the carousel on the way down.
        mousewheel: {
          enabled: true,
          forceToAxis: true,
          releaseOnEdges: true,
          sensitivity: 0.65,
          thresholdDelta: 2,
        },
        navigation: {
          prevEl: this.prevButton,
          nextEl: this.nextButton,
        },
        breakpoints: {
          750: {
            spaceBetween: 0,
          },
        },
        observer: true,
        observeParents: true,
        resizeObserver: true,
      });
    } catch (error) {
      this.dataset.swiperFallback = 'true';
    }
  }

  waitForSwiper() {
    if (window.Swiper) return Promise.resolve(window.Swiper);

    return new Promise((resolve, reject) => {
      const startedAt = performance.now();
      const check = () => {
        if (window.Swiper) {
          resolve(window.Swiper);
          return;
        }
        if (performance.now() - startedAt >= 10000) {
          reject(new Error('Swiper failed to load.'));
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  handleCartUpdate(event) {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const dialog = event.target.closest('[data-primerry-quick-dialog]');
    event.promise?.then(({ detail }) => {
      if (!detail?.didError && dialog?.open) {
        dialog.close();
        document.documentElement.classList.remove('primerry-quick-view-open');
      }
    }).catch(() => {});
  }

  async handleSubmit(event) {
    const form = event.target.closest('[data-primerry-quick-form]');
    if (!form || !this.contains(form)) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-primerry-form-error]');
    if (!button || button.dataset.loading === 'true') return;

    button.dataset.loading = 'true';
    button.disabled = true;
    button.textContent = 'adding…';
    if (error) error.hidden = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
        credentials: 'same-origin',
      });
      const addedItem = await response.json();
      if (!response.ok) throw new Error(addedItem.description || addedItem.message || 'Unable to add this product.');

      const root = window.Shopify?.routes?.root || '/';
      const [cart, renderedSections] = await Promise.all([
        fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((result) => result.json()),
        fetch(`${window.location.pathname}?sections=cart-drawer-section`, { credentials: 'same-origin' }).then((result) => result.json()),
      ]);

      const drawerHtml = renderedSections['cart-drawer-section'];
      const currentDrawer = document.querySelector('#shopify-section-cart-drawer-section');
      if (drawerHtml && currentDrawer) {
        const template = document.createElement('template');
        template.innerHTML = drawerHtml.trim();
        const replacement = template.content.firstElementChild;
        if (replacement) currentDrawer.replaceWith(replacement);
      }

      document.querySelectorAll('.primerry-header__cart-count').forEach((count) => {
        count.textContent = Math.min(cart.item_count, 99).toString();
        count.classList.toggle('primerry-header__cart-count--empty', cart.item_count === 0);
      });

      const dialog = form.closest('[data-primerry-quick-dialog]');
      dialog?.close();
      document.documentElement.classList.remove('primerry-quick-view-open');
      requestAnimationFrame(() => document.querySelector('.primerry-header__cart')?.click());
    } catch (submitError) {
      if (error) {
        error.textContent = submitError instanceof Error ? submitError.message : 'Unable to add this product.';
        error.hidden = false;
      }
    } finally {
      button.dataset.loading = 'false';
      button.disabled = false;
      button.textContent = button.dataset.defaultLabel || 'add to cart';
    }
  }
}

if (!customElements.get('primerry-product-grid')) {
  customElements.define('primerry-product-grid', PrimerryProductGrid);
}
